import { iEmail } from "../../interfaces/emailModel";
import { ungzipToBlob } from "../../utilities/gZip";
import { deleteItem, getItem } from "../../utilities/localStore/indexDBCalls";
import { CallEmsApi } from "../EmsAPI/FetchExtension";
import { createDraftId, saveDraftToIndexedDb, updateDraftId } from "./draft";
import { inlineParts } from "./parts";

export type DraftServerState = "exists" | "gone" | "unknown";

export interface iLocalDraftResult {
    draft?: iEmail;
    attachmentsRemoved: boolean;
    inlineImagesRemoved: boolean;
}

type InlineAttachment = NonNullable<iEmail["inlineAttachments"]>[number];

const pendingChecks = new Map<string, Promise<iLocalDraftResult>>();

// the draft authorizer answers 403 before the handler runs when the draft is missing (or not this user's),
// so asking for part 1 tells us whether the draft is still there: 200/404 means it is, 403 means it is gone.
// on a shared pc a 403 can also be another user's draft left in this tenant's IndexedDB; recovering it gives
// this user a draft of their own and does not touch the other user's.
export const draftExistsOnServer = async (mailDraftId: number): Promise<DraftServerState> => {
    const resp = await CallEmsApi("GET", `Draft/${mailDraftId}/Part/1`, undefined, undefined, true);
    if (resp.status === 200 || resp.status === 404) {
        return "exists";
    }
    if (resp.status === 403) {
        return "gone";
    }
    return "unknown"; // offline or a server error: keep the local draft as it is
};

// loads the local draft for an enquiry action and makes sure the server still has it.
// when the server draft is gone a new one is created from the local copy.
export const getUsableLocalDraft = async (enquiryId: number, actionId: number): Promise<iLocalDraftResult> => {
    const uId = enquiryId + "_" + actionId;
    // one check per draft at a time, otherwise two callers would each create a new server draft
    let pending = pendingChecks.get(uId);
    if (!pending) {
        pending = checkLocalDraft(uId, enquiryId).finally(() => pendingChecks.delete(uId));
        pendingChecks.set(uId, pending);
    }
    const result = await pending;
    return { ...result, draft: result.draft ? { ...result.draft } : undefined };
};

const checkLocalDraft = async (uId: string, enquiryId: number): Promise<iLocalDraftResult> => {
    const draft: iEmail | undefined = await getItem("MailDrafts", uId);
    const result: iLocalDraftResult = { draft, attachmentsRemoved: false, inlineImagesRemoved: false };
    if (!draft || !draft.maildraftId) {
        return result;
    }

    const state = await draftExistsOnServer(draft.maildraftId);
    if (state === "exists") {
        const refreshed = { ...draft, updateDate: new Date() };
        await saveDraftToIndexedDb(refreshed);
        return { ...result, draft: refreshed };
    }
    if (state === "gone") {
        return await recoverLocalDraft(draft, enquiryId);
    }
    return result;
};

const recoverLocalDraft = async (draft: iEmail, enquiryId: number): Promise<iLocalDraftResult> => {
    const ed: iEmail = { ...draft };
    try {
        ed.maildraftId = await createDraftId(ed, enquiryId);
    } catch (err) {
        console.error(err);
        return { draft, attachmentsRemoved: false, inlineImagesRemoved: false };
    }
    ed.bodyPartId = undefined; // the old body part belonged to the old draft, the next save creates a new one

    // attachments are not carried over, the user adds them again.
    // uploads still running in this page belong to the old draft and fail on their own.
    const attachmentsRemoved = (ed.attachments?.length ?? 0) > 0;
    for (const attachment of ed.attachments ?? []) {
        if (attachment.id) {
            await deleteItem("Blobs", attachment.id);
        }
    }
    ed.attachments = [];

    // the new draft only has the recipients and subject, this sends the priority, attached and embedded messages
    await updateDraftId(ed);

    let inlineImagesRemoved = false;
    const recreated: InlineAttachment[] = [];
    for (const inline of ed.inlineAttachments ?? []) {
        const restored = await recreateInlineImage(ed, inline);
        if (restored) {
            recreated.push(restored);
        } else if (inline.mimeContentID && ed.body.indexOf(inline.mimeContentID) > 0) {
            ed.body = removeInlineImage(ed.body, inline.mimeContentID);
            inlineImagesRemoved = true;
        }
    }
    ed.inlineAttachments = recreated;

    const recovered = { ...ed, updateDate: new Date() };
    await saveDraftToIndexedDb(recovered);
    return { draft: recovered, attachmentsRemoved, inlineImagesRemoved };
};

// uploads the image kept in IndexedDB to the new draft under the same content id, so the <img> in the body still matches
const recreateInlineImage = async (ed: iEmail, inline: InlineAttachment): Promise<InlineAttachment | undefined> => {
    if (!inline.id) {
        return undefined;
    }
    const stored = await getItem("Blobs", inline.id);
    await deleteItem("Blobs", inline.id);
    if (!inline.mimeContentID || ed.body.indexOf(inline.mimeContentID) <= 0 || !stored?.data) {
        return undefined;
    }
    try {
        const blob = ungzipToBlob(stored.data, inline.mimeType);
        const newInline = await inlineParts(
            blob,
            ed.maildraftId,
            inline.dataUrl ?? "",
            inline.name,
            inline.mimeContentID,
            true,
        );
        return newInline === "" ? undefined : newInline;
    } catch (err) {
        console.error(err);
        return undefined;
    }
};

const removeInlineImage = (body: string, mimeContentID: string): string => {
    const escaped = mimeContentID.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return body.replace(new RegExp(`<img\\b[^>]*\\btitle=["']${escaped}["'][^>]*>`, "gi"), "");
};
